import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import { useAuth } from '../hooks/useAuth';
import { usePackages } from '../hooks/usePackages';
import { Plus, Edit, Trash2, Eye, Package } from 'lucide-react';
import { TrainingPackage } from '../types';
import CreatePackageModal from '../components/CreatePackageModal';
import EditPackageModal from '../components/EditPackageModal';
import ViewPackageModal from '../components/ViewPackageModal';

const PackagesPage: React.FC = () => {
  const { trainer, logout } = useAuth();
  const { packages, isLoading, deletePackage, isDeleteLoading } = usePackages();
  
  // Состояния для модальных окон
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<TrainingPackage | null>(null);

  const handleEditPackage = (pkg: TrainingPackage) => {
    setSelectedPackage(pkg);
    setIsEditModalOpen(true);
  };

  const handleDeletePackage = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот пакет?')) {
      deletePackage(id);
    }
  };

  const handleViewPackage = (pkg: TrainingPackage) => {
    setSelectedPackage(pkg);
    setIsViewModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedPackage(null);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedPackage(null);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation currentPage="packages" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Пакеты тренировок</h1>
              <p className="mt-2 text-gray-600">
                Создавайте и управляйте пакетами тренировок для своих клиентов
              </p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Создать пакет
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <Package className="h-12 w-12" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Нет пакетов</h3>
            <p className="mt-1 text-sm text-gray-500">
              Создайте первый пакет тренировок, нажав кнопку "Создать пакет".
            </p>
            <div className="mt-6">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Создать пакет
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg: TrainingPackage) => (
              <div
                key={pkg.id}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {pkg.name}
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewPackage(pkg)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Просмотр"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditPackage(pkg)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                        title="Редактировать"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePackage(pkg.id)}
                        disabled={isDeleteLoading}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200 disabled:opacity-50"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {pkg.description && (
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2 mb-3">
                      {pkg.description}
                    </p>
                  )}
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Тренировок:</span>
                      <span className="font-semibold text-gray-900">{pkg.sessions_count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Цена:</span>
                      <span className="font-semibold text-green-600">{formatPrice(pkg.price)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Активных клиентов:</span>
                      <span className="font-semibold text-blue-600">{pkg.active_clients_count || 0}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      pkg.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {pkg.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                    <span>
                      {new Date(pkg.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальные окна */}
      <CreatePackageModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
      />
      
      <EditPackageModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        package={selectedPackage}
      />
      
      <ViewPackageModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        package={selectedPackage}
      />
    </div>
  );
};

export default PackagesPage;